import { customInstance, apiProblemOf } from "@repo/api-client";

/**
 * Client contract for the landed staff floor-plan REST surface (backend `FloorPlanController`,
 * DOMAIN.md §C.5). Goes through `@repo/api-client`'s fetch mutator (bearer token + typed `ApiError`),
 * NOT a raw fetch — a 409 stale write comes back as an `ApiError` carrying the RFC 9457 problem with
 * `errorCode = "STALE_WRITE"` + the authoritative `current` plan, and a typed rejection
 * (ZONE_REDUCED_BELOW_COMMITTED / INVALID_COMBINATION / FLOOR_PLAN_ENTITLEMENT_REQUIRED) is a
 * problem+json. These thin accessors mirror the committed `scheduleApi`/`onboardingApi` stand-in
 * pattern: the frontend's `openapi.json` is a placeholder without the floor endpoints, so a
 * hand-written accessor (NOT an edit of the generated `src/generated`) is how every landed staff
 * feature consumes its controller until the spec is refreshed by the backend build (`just gen`).
 */

// --- wire DTOs (1:1 with FloorPlanController) --------------------------------------------------

export type ZoneKindDto = "SEATED_TABLES" | "STANDING_HEADCOUNT";
export type TableShapeDto = "ROUND" | "SQUARE" | "RECTANGLE" | "OVAL" | "CUSTOM";
export type CapacityModeDto = "CAPACITY_ONLY" | "FLOOR_PLAN";

export interface ViewportDto {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ZoneResponse {
  id: string;
  name: string;
  kind: string;
  capacity: number | null;
  legalCapacity: number | null;
}

export interface TableResponse {
  id: string;
  zoneId: string;
  label: string;
  shape: string;
  x: number;
  y: number;
  rotationDeg: number;
  minCovers: number;
  maxCovers: number;
  priorityOrder: number;
  retired: boolean;
}

export interface CombinableDto {
  tableA: string;
  tableB: string;
}

export interface AutoAssignPolicyResponse {
  oversizeCap: number;
  spreadAcrossZones: boolean;
}

export interface FloorPlanResponse {
  mode: string;
  totalCovers: number | null;
  version: number;
  zones: ZoneResponse[];
  tables: TableResponse[];
  combinables: CombinableDto[];
  policy: AutoAssignPolicyResponse;
}

export interface ZoneRequest {
  id?: string | null;
  name: string;
  kind: ZoneKindDto;
  capacity?: number | null;
  legalCapacity?: number | null;
  viewport?: ViewportDto | null;
}

export interface TableRequest {
  id?: string | null;
  zoneId: string;
  label: string;
  shape: TableShapeDto;
  x: number;
  y: number;
  rotationDeg: number;
  minCovers: number;
  maxCovers: number;
  priorityOrder: number;
}

export interface SaveFloorPlanRequest {
  version: number;
  zones: ZoneRequest[];
  tables: TableRequest[];
  combinables: CombinableDto[];
  policy: { oversizeCap: number; spreadAcrossZones: boolean } | null;
}

export interface AutoAssignPolicyRequest {
  oversizeCap: number;
  spreadAcrossZones: boolean;
  version: number;
}

export interface CapacityOnlyModeRequest {
  totalCovers: number;
  version: number;
}

const base = (venueId: string) => `/api/v1/venues/${encodeURIComponent(venueId)}`;

export const floorApi = {
  getPlan: (venueId: string) =>
    customInstance<FloorPlanResponse>(`${base(venueId)}/floor-plan`, { method: "GET" }),

  /** Whole-aggregate CAS save (FL-6). Also performs the forward FL-7 mode switch server-side. */
  savePlan: (venueId: string, body: SaveFloorPlanRequest) =>
    customInstance<FloorPlanResponse>(`${base(venueId)}/floor-plan`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),

  savePolicy: (venueId: string, body: AutoAssignPolicyRequest) =>
    customInstance<FloorPlanResponse>(`${base(venueId)}/floor-plan/auto-assign-policy`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),

  /** Reverse mode switch (FL-7) — drop the drawn layout back to a capacity-only pool. */
  switchToCapacityOnly: (venueId: string, body: CapacityOnlyModeRequest) =>
    customInstance<FloorPlanResponse>(`${base(venueId)}/floor-plan/mode/capacity-only`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),

  /**
   * The venue's derived entitlement flags (§C.3). The entitlements engine is a separate task; this
   * accessor reads the same `["venue", venueId, "entitlements"]` resource the builder gates on. Its
   * absence is NOT authoritative — the server re-enforces FLOOR_PLAN fail-closed on every write
   * (FloorPlanCommandPort), so a client read failure leaves the editor usable and the write blocked.
   */
  getEntitlements: (venueId: string) =>
    customInstance<VenueEntitlementsResponse>(`${base(venueId)}/entitlements`, { method: "GET" }),
};

export interface VenueEntitlementsResponse {
  flags: Record<string, boolean>;
}

/** The `["venue", venueId, "floorplan"]` query-key family this feature owns. Aligns with the shell's
 *  `eventQueryKeyMap` (`floor.*`/`table.*` → resource `"floorplan"`), so realtime events invalidate it. */
export const floorKeys = {
  plan: (venueId: string) => ["venue", venueId, "floorplan"] as const,
  entitlements: (venueId: string) => ["venue", venueId, "entitlements"] as const,
};

// --- typed-rejection parsing -------------------------------------------------------------------

export interface ZoneReducedRejection {
  errorCode: "ZONE_REDUCED_BELOW_COMMITTED";
  zoneId: string;
  committed: number;
  requestedCapacity: number;
}

export interface InvalidCombinationRejection {
  errorCode: "INVALID_COMBINATION";
  tableA: string;
  tableB: string;
}

export type FloorSaveRejection =
  | ZoneReducedRejection
  | InvalidCombinationRejection
  | { errorCode: "FLOOR_PLAN_ENTITLEMENT_REQUIRED" };

/**
 * Narrow a failed floor-plan save to its typed rejection body (§D.1), or `undefined` when the error
 * is not one of the modeled floor rejections. Drives the zone-panel FL-4 message and the pair-list
 * FL-2 integrity error without ad-hoc body sniffing at the call site.
 */
export function floorRejectionOf(err: unknown): FloorSaveRejection | undefined {
  const problem = apiProblemOf(err) as { errorCode?: string } | undefined;
  const code = problem?.errorCode;
  if (code === "ZONE_REDUCED_BELOW_COMMITTED") {
    const p = problem as unknown as ZoneReducedRejection;
    return {
      errorCode: code,
      zoneId: p.zoneId,
      committed: p.committed,
      requestedCapacity: p.requestedCapacity,
    };
  }
  if (code === "INVALID_COMBINATION") {
    const p = problem as unknown as InvalidCombinationRejection;
    return { errorCode: code, tableA: p.tableA, tableB: p.tableB };
  }
  if (code === "FLOOR_PLAN_ENTITLEMENT_REQUIRED") return { errorCode: code };
  return undefined;
}
